import Box from "@mui/material/Box";
import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useCreateSettingModuleMutation } from "entities/brands/settings/mutations/useCreateSettingModuleMutation";
import { useUpdateSettingModuleMutation } from "entities/brands/settings/mutations/useUpdateSettingModuleMutation";
import { invalidateEntireModuleListQuery } from "entities/modules/queries/useModuleListByTagIdQuery";
import { useSelectedGamesData } from "entities/modules/stores/useSelectedGamesData";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import { ModuleType } from "entities/modules/types";
import {
  PageModuleDataToCreate,
  PageModuleDataToUpdate,
  PageModuleStatus,
} from "entities/pages/types";
import React, { FC, useMemo } from "react";
import { TagBlock } from "widgets/ModuleTagsWidget/type";
import { getTagsMatchRules } from "widgets/ModuleTagsWidget/utils";
import { useShallow } from "zustand/react/shallow";
import { useSettingModuleDetails } from "../../context";
import { ManagedTranslations } from "../../context/types";
import { useGeneralData } from "../../stores/useGeneralData";
import { useStudioOrderingData } from "../../stores/useStudioOrderingData";
import { useStudiosData } from "../../stores/useStudiosData";
import { StyledPrimaryButton, StyledSecondaryButton } from "./styled";

const ActionButtons: FC = () => {
  const { moduleId, handleDrawerClose, brandId, settingId, moduleData } =
    useSettingModuleDetails();

  const {
    moduleName,
    moduleType,
    moduleLayout,
    adminNotes,
    translations,
    studioFilterEnabled,
    baseUrl,
    isInvalidBaseUrl,
    contentBlocks,
    bonusObtainType,
    rows,
    iconShown,
    icon,
    topPrivacyTipsBlock,
  } = useGeneralData(
    useShallow((state) => ({
      moduleName: state.moduleName,
      moduleType: state.moduleType,
      moduleLayout: state.moduleLayout,
      adminNotes: state.adminNotes,
      translations: state.translations,
      studioFilterEnabled: state.studioFilterEnabled,
      baseUrl: state.baseUrl,
      rows: state.rows,
      isInvalidBaseUrl: state.isInvalidBaseUrl,
      contentBlocks: state.contentBlocks,
      topPrivacyTipsBlock: state.topPrivacyTipsBlock,
      bonusObtainType: state.bonusObtainType,
      iconShown: state.iconShown,
      icon: state.icon,
    })),
  );

  const { selectedGames, orderType, manualSelectedGames, tagSelectedGames } =
    useSelectedGamesData(
      useShallow((state) => ({
        selectedGames: state.selectedGames,
        orderType: state.orderType,
        manualSelectedGames: state.manualSelectedGames,
        tagSelectedGames: state.tagSelectedGames,
      })),
    );

  const tagBlocks = useTagBlocksStore((state) => state.tagBlocks);
  const selectedStudios = useStudiosData((state) => state.selectedStudios);
  const orderedStudios = useStudioOrderingData((state) => state.orderedStudios);

  const areTagsIncorrect = useMemo(() => {
    const hasTagTypeWithoutTags = tagBlocks.some(
      (block) => !block.selectedTags.length,
    );
    return !tagBlocks.length || hasTagTypeWithoutTags;
  }, [tagBlocks]);

  const gamesTabsInvalid = useMemo(() => {
    if (moduleType !== ModuleType.Games) return false;
    if (!moduleLayout) return true;

    return !(manualSelectedGames?.length > 0 || !areTagsIncorrect);
  }, [moduleType, moduleLayout, manualSelectedGames, areTagsIncorrect]);
  const studiosTabsInvalid =
    moduleType === ModuleType.Studios &&
    (!moduleLayout || !selectedStudios.length);
  const productIframeInvalid =
    moduleType === ModuleType.ProductIframe && (!baseUrl || isInvalidBaseUrl);

  const iconNotSelected = iconShown && !icon;

  const areButtonsDisabled =
    !moduleName ||
    !moduleType ||
    gamesTabsInvalid ||
    studiosTabsInvalid ||
    productIframeInvalid ||
    iconNotSelected;

  const { mutateAsync: createModule, isPending: isCreatingModule } =
    useCreateSettingModuleMutation();

  const { mutateAsync: updateModule, isPending: isUpdatingModule } =
    useUpdateSettingModuleMutation();

  const { allowed: hasWriteAccess } = useRolePermission("brandsAndPages:write");
  const { allowed: hasWriteTranslationsAccess } = useRolePermission(
    "brandsAndPages:translations:write",
  );

  const baseUrlToSubmit =
    moduleType === ModuleType.ProductIframe ? baseUrl : "";

  const handleSave = async (status: PageModuleStatus) => {
    if (areButtonsDisabled) {
      return;
    }

    const tagTypeIds = new Set<string>();
    const tagIds = new Set<string>();

    tagBlocks.forEach((block: TagBlock) => {
      if (block.primaryTagId) {
        tagTypeIds.add(block.primaryTagId);
      }

      block.selectedTags.forEach((tag) => {
        tagIds.add(tag.id);
      });
    });

    const translationsWithDefaultValues = {
      ...translations,
      en: {
        ...translations?.en,
        [ManagedTranslations.ModuleName]: moduleName,
      },
    };

    const selectedGamesIdsByStudio = selectedGames.reduce(
      (acc: Record<string, string[]>, game) =>
        Object.assign(acc, {
          [String(game.studioId)]: [
            ...(acc[String(game.studioId)] ?? []),
            String(game.id),
          ],
        }),
      {},
    );

    const dataToSet: PageModuleDataToCreate = {
      name: moduleName,
      type: moduleType,
      layout: moduleLayout,
      status,
      rows,
      adminNotes: adminNotes || null,
      translations: translationsWithDefaultValues,
      tagTypes: Array.from(tagTypeIds),
      tags: Array.from(tagIds),
      gamesOrderType: orderType,
      manualSelectedGames:
        manualSelectedGames?.map((game) => ({
          id: game.id,
          status: game.status,
        })) || [],
      tagSelectedGames:
        tagSelectedGames?.map((game) => ({
          id: game.id,
          status: game.status,
        })) || [],
      selectedGames:
        selectedGames?.map((game) => ({
          id: game.id,
          status: game.status,
        })) || [],
      selectedStudiosIds: selectedStudios?.map((studio) => studio.id) ?? [],
      selectedGamesIdsByStudio,
      order: moduleData?.order ?? 1,
      studioFilterEnabled,
      orderedGamesStudiosIds: orderedStudios.map((studio) =>
        studio.id.toString(),
      ),
      baseUrl: baseUrlToSubmit,
      contentBlocks:
        contentBlocks?.map((block) => ({
          ...block,
          url: block.ctaDestination === "game" ? "" : block.url || "",
        })) ?? [],
      topPrivacyTipsBlock: topPrivacyTipsBlock,
      bonusObtainType: bonusObtainType ?? null,
      icon,
      tagsMatchRules: getTagsMatchRules(tagBlocks),
    };

    if (moduleId) {
      await updateModule({
        moduleId,
        moduleData: dataToSet,
        brandId,
        settingId,
      });
    } else {
      await createModule({
        data: dataToSet,
        brandId,
        settingId,
      });
    }

    handleDrawerClose();
    invalidateEntireModuleListQuery();
  };

  const handleSaveTranslations = async () => {
    if (!moduleId) {
      return;
    }

    const translationsWithDefaultValues = {
      ...translations,
      en: {
        ...translations?.en,
        [ManagedTranslations.ModuleName]: moduleName,
      },
    };

    const dataToSet: PageModuleDataToUpdate = {
      translations: translationsWithDefaultValues,
    };

    await updateModule({
      moduleId,
      moduleData: dataToSet,
      brandId,
      settingId,
    });

    handleDrawerClose();
    invalidateEntireModuleListQuery();
  };

  const handleSaveDraft = () => handleSave(PageModuleStatus.Draft);

  const handleSaveActive = () => handleSave(PageModuleStatus.Active);

  if (!hasWriteAccess && !hasWriteTranslationsAccess) {
    return null;
  }

  if (!hasWriteAccess && hasWriteTranslationsAccess) {
    return (
      <Box gap="1rem" display="flex" flexDirection="row" alignItems="center">
        <StyledPrimaryButton
          title="Save"
          size="small"
          disabled={areButtonsDisabled}
          onClick={handleSaveTranslations}
          loading={isCreatingModule || isUpdatingModule}
        />
      </Box>
    );
  }

  return (
    <Box gap="1rem" display="flex" flexDirection="row" alignItems="center">
      <StyledSecondaryButton
        title="Save as draft"
        size="small"
        color="secondary"
        variant="outlined"
        disabled={areButtonsDisabled}
        onClick={handleSaveDraft}
        loading={isCreatingModule || isUpdatingModule}
      />
      <StyledPrimaryButton
        title="Save and Publish"
        size="small"
        disabled={areButtonsDisabled}
        onClick={handleSaveActive}
        loading={isCreatingModule || isUpdatingModule}
      />
    </Box>
  );
};

export default ActionButtons;
